import { CssBaseline, Grid, Paper, Typography, WithStyles, withStyles } from "@material-ui/core";
import { styles } from "../RegisterView.styles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import * as SubscriptionAction from "../../../stores/subscriptions/SubscriptionAction";
import { multipleEmailsPattern } from "../../../utilities/generalUtils";
import { FormTextField } from "../../components/FormComponents/FormTextField";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { useAuthContext } from "../../../auth/AuthProvider";
import { useParams } from "react-router-dom";
import { PageBanner } from "../../components/PageBanner/PageBanner";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import SubscriptionUserModel from "../../../models/subscription/SubscriptionUserModel";
import SubscriptionModel from "../../../models/subscription/SubscriptionModel";

export interface ISendEmailToActiveUsersViewProps extends WithStyles<typeof styles> {}

const SendEmailToActiveUsersViewPure: React.FunctionComponent<ISendEmailToActiveUsersViewProps> = ({ classes }) => {
    const { t } = useTranslation();
    const authContext = useAuthContext();
    const userId = authContext?.user?.id;
    const [errors, setErrors] = useState({} as any);
    const dispatch = useDispatch();
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);
    const { subscriptionId } = useParams();

    const subscriptionsFromState = useSelector(state => state.subscriptions);

    const [details, setDetails] = React.useState({
        recipients: "",
        additionalRecipients: "",
        subject: "",
        message: ""
    });

    let currentSubscription: any;
    if (subscriptionsFromState?.allSubscriptions) {
        currentSubscription = Object.values(subscriptionsFromState?.allSubscriptions)?.find(
            (subscription: SubscriptionModel) => subscription.id === parseInt(subscriptionId)
        );
    }

    const subscriptionName = currentSubscription ? currentSubscription?.name : "";
    const subscriptionFullTitle = currentSubscription
        ? `${currentSubscription?.name} - ${currentSubscription?.description}`
        : "";

    let activeUsers: string[] = [];
    if (subscriptionsFromState?.subscriptionUsers) {
        Object.values(subscriptionsFromState?.subscriptionUsers)
            ?.filter(
                (subscriptionUser: SubscriptionUserModel) =>
                    subscriptionUser.isSubscriptionUnrestricted || subscriptionUser.isSubscriptionActive
            )
            .forEach((subscriptionUser: SubscriptionUserModel) => {
                const userEmails = [
                    subscriptionUser.email,
                    ...(subscriptionUser?.additionalEmail2 !== "" ? [subscriptionUser?.additionalEmail2] : []),
                    ...(subscriptionUser?.additionalEmail3 !== "" ? [subscriptionUser?.additionalEmail3] : [])
                ];
                activeUsers.push(...userEmails);
            });
    }
    const uniqueActiveUsers = [...new Set(activeUsers)];

    useEffect(() => {
        if (userId) dispatch(UserDetailsAction.requestCurrentUser(userId));
        dispatch(SubscriptionAction.requestSubscriptionUsers(subscriptionId));
    }, [dispatch, userId, subscriptionId]);

    useEffect(() => {
        if (userDetails)
            setDetails(details => ({
                ...details,
                recipients: uniqueActiveUsers?.join("; "),
                email: userDetails?.email
            }));
    // eslint-disable-next-line
    }, [subscriptionsFromState]);

    const handleChange = event => {
        const name = event.target.name;
        const value = event.target.value;
        setDetails({
            ...details,
            [name]: value
        });

        let temp: any = { ...errors };
        if (event.target.name === "additionalRecipients") {
            if (event.target.value) {
                temp[event.target.name] = multipleEmailsPattern.test(event.target.value) ? "" : "Email is not valid!";
            }
        }
        setErrors({
            ...temp
        });
    };

    const formIsValid = () => {
        return (
            details.recipients &&
            Object.values(errors).every(x => x === "")
        );
    };

    const sendContactRequest = () => {
        const outlookLink = "mailto:?bcc=" + details.recipients;
        window.location.href = outlookLink;
    };

    return (
        <>
            <PageBanner title="Send email to active users" />
            <CenteredContainer>
                <CssBaseline />
                <Breadcrumbs
                    items={[
                        { title: t("admin.adminPanel"), href: "/admin" },
                        {
                            title: t("admin.manageSubscriptionsView.manageSubscriptions"),
                            href: "/admin/manage-subscriptions"
                        },
                        {
                            title: `${t(
                                "admin.manageSubscriptionUserView.manageSubscriptionUsers"
                            )} - ${subscriptionName}`,
                            href: `/admin/manage-subscriptions/${subscriptionId}`
                        },
                        { title: "Send email to active users" }
                    ]}
                />
                <Paper className={classes.background}>
                    <Grid container spacing={2}>
                        <Grid item xs={8}>
                            <Typography variant="h3" gutterBottom>
                                Send email to active users - {subscriptionFullTitle}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                You can compose an email that will be sent to subscription's active users. Please be aware that the email addresses in the field <b>Recipients</b> are filled automatically - they contain all active users of the subscription. These addresses will be used as BCC.
                            </Typography>
                            <Typography variant="caption" style={{ fontWeight: "bold" }}>
                                Email addresses must be separated by the semicolon!
                            </Typography>
                        </Grid>

                        <Grid item xs={8}>
                            <FormTextField
                                id="recipients"
                                onChange={handleChange}
                                value={details.recipients}
                                label="Recipients - BCC"
                                multiline
                                rows={10}
                                pattern={multipleEmailsPattern}
                                required
                            />
                        </Grid>

                        <Grid item xs={8}>
                            <Typography variant="caption" style={{ fontWeight: "bold" }}>
                                The new Outlook window will be opened, when the "Send" button is clicked. Please, set the correct sender address, enter the subject and the message body.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <BlueButton onClick={sendContactRequest} disabled={!formIsValid()}>
                                {t("contactUs.send")}
                            </BlueButton>
                        </Grid>
                    </Grid>
                </Paper>
            </CenteredContainer>
        </>
    );
};

export const SendEmailToActiveUsersView = withStyles(styles)(SendEmailToActiveUsersViewPure);
